import { useState, useCallback, useEffect, useMemo } from 'react';
import { Sparkles, Send, TrendingUp, AlertTriangle, Target, Zap, FileText, MessageCircle, BarChart3, Loader2 } from 'lucide-react';
import { HeroSection } from '../HeroSection';
import { sendChatMessage, type ChatMessage as ApiChatMessage } from '../../services/aiChat';
import { aiInsightsService, type AIInsight } from '../../services/aiInsights';
import { toast } from 'sonner';

type ChatEntry = { role: 'user' | 'assistant'; content: string; timestamp: string };

// Display impact derived from urgency — keeps the existing visual language
// (Positive / Warning / Negative dot color) without inventing new fields.
function urgencyToImpact(urgency: string | null | undefined): 'Positive' | 'Warning' | 'Negative' {
  if (urgency === 'critical' || urgency === 'high') return 'Negative';
  if (urgency === 'medium') return 'Warning';
  return 'Positive';
}

function formatConfidence(c: number | null | undefined): string {
  if (c == null) return '—';
  const pct = Math.round(c * 100);
  return `${Math.max(0, Math.min(100, pct))}%`;
}

function formatTimestamp() {
  return new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

interface AIAssistantSectionProps {
  projectId?: number
  programId?: number
}

export function AIAssistantSection({ projectId, programId }: AIAssistantSectionProps) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([
    {
      role: 'assistant',
      content: "Hello! I'm your PMO AI Assistant. I can help you with project insights, risk analysis, resource optimization, and PMI best practices. How can I assist you today?",
      timestamp: formatTimestamp(),
    },
  ]);

  // Pull the latest persisted insights for whichever entity owns this panel.
  // Falls back to portfolio-wide when no project/program is in scope.
  useEffect(() => {
    let cancelled = false;
    setInsightsLoading(true);
    const params: { entity_type?: string; entity_id?: number; limit: number } = { limit: 5 };
    if (projectId) {
      params.entity_type = 'project';
      params.entity_id = projectId;
    } else if (programId) {
      params.entity_type = 'program';
      params.entity_id = programId;
    }
    aiInsightsService
      .list(params)
      .then((rows) => {
        if (cancelled) return;
        setInsights(rows ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        // Per feedback_silent_fallback: empty array + toast, never mock.
        setInsights([]);
        toast.error('Failed to load AI insights');
      })
      .finally(() => {
        if (!cancelled) setInsightsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, programId]);

  // Hero metrics — derived from real state, not literals.
  // Conversations = user turns in this session (resets per mount, honest).
  // Insights = count from API; Reports / Confidence avg from same payload.
  const heroMetrics = useMemo(() => {
    const conversationCount = chatHistory.filter((m) => m.role === 'user').length;
    const insightsGenerated = insights.length;
    const reportsCreated = insights.filter((i) => i.type === 'recommendation').length;
    const confidenceVals = insights
      .map((i) => i.confidence)
      .filter((c): c is number => typeof c === 'number');
    const avgConfidence = confidenceVals.length
      ? Math.round((confidenceVals.reduce((sum, c) => sum + c, 0) / confidenceVals.length) * 100)
      : null;
    return { conversationCount, insightsGenerated, reportsCreated, avgConfidence };
  }, [chatHistory, insights]);

  const sendPrompt = useCallback(async (text: string) => {
    if (!text.trim() || loading) return;

    const userEntry: ChatEntry = { role: 'user', content: text.trim(), timestamp: formatTimestamp() };
    setChatHistory((prev) => [...prev, userEntry]);
    setMessage('');
    setLoading(true);

    try {
      const apiMessages: ApiChatMessage[] = chatHistory.map((m) => ({ role: m.role, content: m.content }));
      apiMessages.push({ role: 'user', content: text.trim() });
      const res = await sendChatMessage(apiMessages, {
        projectId,
        programId,
      });
      setChatHistory((prev) => [
        ...prev,
        { role: 'assistant', content: res.message, timestamp: formatTimestamp() },
      ]);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : null;
      toast.error(msg || 'Failed to get AI response');
      setChatHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I could not get a response right now. Please check your connection and try again.',
          timestamp: formatTimestamp(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [loading, chatHistory]);

  const handleSend = useCallback(() => {
    sendPrompt(message);
  }, [message, sendPrompt]);

  const quickActions = [
    { icon: TrendingUp, label: 'Portfolio Health Summary', color: 'blue', prompt: 'Give me a brief portfolio health summary and what to watch.' },
    { icon: AlertTriangle, label: 'Risk Analysis', color: 'red', prompt: 'What are the key risk analysis techniques I should use for my project?' },
    { icon: Target, label: 'Schedule Optimization', color: 'green', prompt: 'How can I optimize my project schedule and handle delays?' },
    { icon: FileText, label: 'Generate Report', color: 'purple', prompt: 'What should I include in a project status report for stakeholders?' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Hero Section */}
      <div className="flex-shrink-0 p-6 pb-0">
        <HeroSection
          title="AI Assistant"
          description="Intelligent PMO assistant for insights, analysis, and recommendations"
          variant="level4"
          metrics={[
            { label: 'Conversations', value: heroMetrics.conversationCount.toString(), icon: MessageCircle, iconColor: 'bg-blue-50' },
            { label: 'Insights Generated', value: heroMetrics.insightsGenerated.toString(), icon: Sparkles, iconColor: 'bg-purple-50' },
            { label: 'Recommendations', value: heroMetrics.reportsCreated.toString(), icon: FileText, iconColor: 'bg-green-50' },
            { label: 'Avg Confidence', value: heroMetrics.avgConfidence == null ? '—' : `${heroMetrics.avgConfidence}%`, icon: BarChart3, iconColor: 'bg-orange-50' },
          ]}
        />
      </div>

      {/* Chat Interface */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {chatHistory.map((msg, index) => (
              <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'} rounded-lg p-4`}>
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-medium text-gray-600">AI Assistant</span>
                    </div>
                  )}
                  <div className="whitespace-pre-wrap font-['Arial:Regular',sans-serif] text-[14px] leading-[20px]">
                    {msg.content}
                  </div>
                  <div className={`text-xs mt-2 ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {msg.timestamp}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Input Area */}
          <div className="bg-white border-t border-gray-200 p-4">
            <form
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Ask me anything about your portfolio..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 font-['Arial:Regular',sans-serif] text-[14px] text-gray-900"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !message.trim()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span>{loading ? 'Sending...' : 'Send'}</span>
              </button>
            </form>

            {/* Quick Actions */}
            <div className="grid grid-cols-4 gap-2 mt-3">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    type="button"
                    disabled={loading}
                    onClick={() => sendPrompt(action.prompt)}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors disabled:opacity-50 text-left"
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${
                      action.color === 'blue' ? 'text-blue-600' :
                      action.color === 'red' ? 'text-red-600' :
                      action.color === 'green' ? 'text-green-600' :
                      'text-purple-600'
                    }`} />
                    <span className="text-xs text-gray-700 truncate">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Sidebar - AI Insights */}
        <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-yellow-600" />
              <h2 className="text-lg">AI Insights</h2>
            </div>

            <div className="space-y-4">
              {insightsLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-xs">Loading insights…</span>
                </div>
              ) : insights.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs">No insights yet. Ask the assistant a question to generate one.</p>
                </div>
              ) : (
                insights.map((insight) => {
                  const impact = urgencyToImpact(insight.urgency);
                  return (
                    <div key={insight.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex items-start gap-3 mb-2">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          impact === 'Positive' ? 'bg-green-500' :
                          impact === 'Warning' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`} />
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-neutral-950 mb-1">{insight.title}</h3>
                          <p className="text-xs text-gray-600">{insight.reasoning || insight.content}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Confidence: {formatConfidence(insight.confidence)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}